import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { TokenResponse } from '@wl/ui';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, sessionEvents } from './api';
import type { PlatformAdmin, PlatformRole } from './types';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthContextValue {
  status: Status;
  admin: PlatformAdmin | null;
  /** Şifre + authenticator kodu tek adımda. */
  login: (email: string, password: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
export const ME_KEY = ['platform', 'me'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [token, setToken] = useState<string | null>(api.getToken());
  const [booted, setBooted] = useState(false);

  useEffect(() => api.onTokenChange(setToken), []);
  useEffect(() => {
    let cancelled = false;
    void api.refresh().finally(() => !cancelled && setBooted(true));
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    const onExpired = () => {
      api.setToken(null);
      qc.clear();
    };
    sessionEvents.addEventListener('expired', onExpired);
    return () => sessionEvents.removeEventListener('expired', onExpired);
  }, [qc]);

  const me = useQuery({ queryKey: ME_KEY, queryFn: () => api.get<PlatformAdmin>('/platform/auth/me'), enabled: booted && !!token, staleTime: 5 * 60_000 });

  const login = useCallback(
    async (email: string, password: string, code: string) => {
      const t = await api.post<TokenResponse>('/platform/auth/login', { email, password, code }, { skipRefresh: true });
      api.setToken(t.accessToken);
      await qc.fetchQuery({ queryKey: ME_KEY, queryFn: () => api.get<PlatformAdmin>('/platform/auth/me') });
    },
    [qc],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/platform/auth/logout', undefined, { skipRefresh: true });
    } finally {
      api.setToken(null);
      qc.clear();
    }
  }, [qc]);

  const status: Status = !booted ? 'loading' : !token ? 'anonymous' : me.data ? 'authenticated' : me.isError ? 'anonymous' : 'loading';
  const value = useMemo(() => ({ status, admin: me.data ?? null, login, logout }), [status, me.data, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('AuthProvider eksik');
  return ctx;
}

export function useAdmin(): PlatformAdmin {
  const { admin } = useAuth();
  if (!admin) throw new Error('Oturum yok');
  return admin;
}

/** Yazma işlemleri owner/admin rolüne açık; support yalnızca görüntüler. */
export function useCanWrite(): boolean {
  return canWrite(useAdmin().role);
}

export function canWrite(role: PlatformRole): boolean {
  return role === 'owner' || role === 'admin';
}
